import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, AlertTriangle, Shield, Mail } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 80px 20px 60px;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #FAECEC;
  opacity: 0.7;
  font-size: 0.95rem;
`;

const Content = styled(motion.div)`
  background: rgba(0, 151, 178, 0.05);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  padding: 50px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #0097B2;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Paragraph = styled.p`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-bottom: 16px;
  font-size: 1rem;
`;

const List = styled.ul`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-left: 24px;
  margin-bottom: 16px;
`;

const ListItem = styled.li`
  margin-bottom: 12px;
`;

const Highlight = styled.span`
  color: #0097B2;
  font-weight: 600;
`;

const WarningCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 159, 64, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 2px solid rgba(255, 159, 64, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h4`
  color: #FAECEC;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ContactEmail = styled.a`
  color: #0097B2;
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TermsOfService = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Terms of Service | AI Crypto Trading Bot Platform</title>
        <meta name="description" content="Read the Terms of Service for f01i.ai/f01i.app. Learn about eligibility, API keys, account security, risk disclosures, and user responsibilities." />
        <link rel="canonical" href="https://f01i.ai/terms-of-service" />
      </Helmet>
      <Container>
        <BackLink to="/">
          <ArrowLeft size={20} />
          Back to Home
        </BackLink>

        <Header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>Terms of Service</Title>
          <LastUpdated>Last updated: January 2026</LastUpdated>
        </Header>

        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section>
            <Paragraph>
              Welcome to <Highlight>f01i.ai</Highlight> and <Highlight>f01i.app</Highlight>. By accessing or using our 
              AI-powered trading platform and services, you agree to be bound by these Terms of Service. 
              Please read them carefully.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>
              <FileText size={24} />
              1. Acceptance of Terms
            </SectionTitle>
            <Paragraph>
              By creating an account, accessing our website, or using any of our services, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these 
              terms, please do not use our services.
            </Paragraph>
          </Section>

          <WarningCard>
            <SectionTitle>
              <AlertTriangle size={24} color="#FF9F40" />
              Important Trading Disclaimer
            </SectionTitle>
            <Paragraph>
              <Highlight>Trading cryptocurrencies involves substantial risk and may result in significant financial loss.</Highlight> 
              Our AI-powered tools and automated trading bots are provided for informational and automation purposes only. 
              Past performance does not guarantee future results. You are solely responsible for your trading decisions and any 
              resulting profits or losses.
            </Paragraph>
          </WarningCard>

          <Section>
            <SectionTitle>2. Eligibility</SectionTitle>
            <Paragraph>
              To use our services, you must:
            </Paragraph>
            <List>
              <ListItem>Be at least 18 years of age or the age of majority in your jurisdiction</ListItem>
              <ListItem>Have the legal capacity to enter into a binding agreement</ListItem>
              <ListItem>Not be prohibited from using our services under applicable laws</ListItem>
              <ListItem>Comply with all local laws and regulations regarding cryptocurrency trading</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>
              <Shield size={24} />
              3. Account Security & API Keys
            </SectionTitle>
            <Paragraph>
              You are responsible for maintaining the security of your account credentials and API keys:
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>Encrypted Storage:</Highlight> We use military-grade encryption to protect your exchange API keys. 
                However, you must ensure your API keys are configured correctly.
              </ListItem>
              <ListItem>
                <Highlight>Read-Only Keys Only:</Highlight> You must only provide read-only API keys without withdrawal permissions. 
                We can only read your data — we cannot and will not withdraw or transfer your funds.
              </ListItem>
              <ListItem>
                <Highlight>Your Responsibility:</Highlight> You are solely responsible for any activities conducted through your account. 
                Notify us immediately if you suspect unauthorized access.
              </ListItem>
              <ListItem>
                <Highlight>No Fund Access:</Highlight> We never store or access your funds. Your assets remain completely under your control 
                at all times.
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Use of Services</SectionTitle>
            <Paragraph>
              When using our platform, you agree to:
            </Paragraph>
            <List>
              <ListItem>Use the services for lawful purposes only</ListItem>
              <ListItem>Not attempt to circumvent security measures or gain unauthorized access</ListItem>
              <ListItem>Not interfere with or disrupt the integrity of our systems</ListItem>
              <ListItem>Not use automated systems to scrape or harvest data without permission</ListItem>
              <ListItem>Not impersonate others or provide false information</ListItem>
              <ListItem>Not engage in market manipulation or fraudulent trading activities</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>5. AI Bot Creator & Trading Bots</SectionTitle>
            <Paragraph>
              Our AI Bot Creator powered by LLMs (Claude, OpenAI, xAI, Gemini, DeepSeek) and automated trading bots are provided "as is":
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>No Guarantees:</Highlight> We do not guarantee profits, performance, or specific outcomes. 
                Trading strategies may perform differently in live markets.
              </ListItem>
              <ListItem>
                <Highlight>User Responsibility:</Highlight> You are responsible for reviewing, testing, and monitoring your bots. 
                We recommend starting with small amounts.
              </ListItem>
              <ListItem>
                <Highlight>Market Risks:</Highlight> Cryptocurrency markets are highly volatile. Automated trading can result in 
                rapid losses during market swings.
              </ListItem>
              <ListItem>
                <Highlight>Technical Issues:</Highlight> Bot execution may be affected by exchange downtime, API issues, network problems, 
                or system maintenance.
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>6. f01i AI Agent & Technical Analysis</SectionTitle>
            <Paragraph>
              Our f01i AI Agent provides technical analysis and market insights:
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>Not Financial Advice:</Highlight> All analysis, recommendations, and insights are for informational purposes only 
                and should not be construed as financial or investment advice.
              </ListItem>
              <ListItem>
                <Highlight>Do Your Research:</Highlight> Always conduct your own research and consult with financial professionals before 
                making trading decisions.
              </ListItem>
              <ListItem>
                <Highlight>Accuracy Disclaimer:</Highlight> While we strive for accuracy, market data and analysis may contain errors or 
                become outdated rapidly.
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>7. f01i Blocks</SectionTitle>
            <Paragraph>
              Our f01i Blocks feature allows you to create comprehensive custom AI trading strategies:
            </Paragraph>
            <List>
              <ListItem>Strategies created using Blocks are your responsibility to test and validate</ListItem>
              <ListItem>Third-party data integrations (Coinglass, CoinMarketCap, CoinGecko) are subject to their respective terms</ListItem>
              <ListItem>You are responsible for evaluating strategies before deployment</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. Fees and Payments</SectionTitle>
            <Paragraph>
              We charge fees for certain services:
            </Paragraph>
            <List>
              <ListItem><Highlight>No Trading Commissions:</Highlight> We charge zero commissions on your trades</ListItem>
              <ListItem><Highlight>Execution-Based Pricing:</Highlight> You pay only for bot executions according to your plan</ListItem>
              <ListItem>Fees are clearly displayed before purchase or service usage</ListItem>
              <ListItem>All payments are processed securely through our payment partners</ListItem>
              <ListItem>We accept cryptocurrency payments via NowPayments</ListItem>
              <ListItem>Fees are non-refundable except as required by law or our refund policy</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>9. Refund Policy</SectionTitle>
            <Paragraph>
              As a digital service provider, we offer refunds under the following conditions:
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>30-Day Refund Window:</Highlight> You may request a refund within 30 days of your initial subscription purchase.
              </ListItem>
              <ListItem>
                <Highlight>Pro-Rata Calculation:</Highlight> Refunds are calculated based on the unused portion of your subscription. 
                The refund amount equals: (Paid Amount) - (Daily Rate × Days Used).
              </ListItem>
              <ListItem>
                <Highlight>Digital Delivery:</Highlight> As our service is delivered digitally and instantly upon payment, 
                full refunds may not be available after service activation if significant usage has occurred.
              </ListItem>
              <ListItem>
                <Highlight>No Refunds For:</Highlight> Execution credits that have been consumed, third-party API costs incurred, 
                or accounts terminated due to Terms of Service violations.
              </ListItem>
              <ListItem>
                <Highlight>Refund Process:</Highlight> To request a refund, contact us at feedback@f01i.ai with your account details 
                and reason for the refund request. Refunds are processed within 5-10 business days.
              </ListItem>
            </List>
            <Paragraph>
              <Highlight>Cancellation:</Highlight> You may cancel your subscription at any time through your account settings. 
              Upon cancellation, you will retain access to your subscription benefits until the end of the current billing period.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>10. Intellectual Property</SectionTitle>
            <Paragraph>
              All content, features, and functionality of our platform are owned by f01i.ai and protected by international 
              copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or reverse 
              engineer any part of our services without express written permission.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. Limitation of Liability</SectionTitle>
            <Paragraph>
              To the maximum extent permitted by law:
            </Paragraph>
            <List>
              <ListItem>We are not liable for any trading losses, missed opportunities, or financial damages</ListItem>
              <ListItem>We are not responsible for exchange downtimes, API failures, or third-party service issues</ListItem>
              <ListItem>Our total liability is limited to the amount you paid for our services in the past 12 months</ListItem>
              <ListItem>We are not liable for indirect, incidental, consequential, or punitive damages</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>12. Indemnification</SectionTitle>
            <Paragraph>
              You agree to indemnify and hold harmless f01i.ai, its officers, directors, employees, and agents from any claims, 
              losses, damages, liabilities, and expenses arising from your use of our services, violation of these terms, or 
              infringement of any rights of another party.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. Termination</SectionTitle>
            <Paragraph>
              We reserve the right to suspend or terminate your account at any time for:
            </Paragraph>
            <List>
              <ListItem>Violation of these Terms of Service</ListItem>
              <ListItem>Fraudulent or illegal activities</ListItem>
              <ListItem>Extended periods of inactivity</ListItem>
              <ListItem>Any reason at our sole discretion</ListItem>
            </List>
            <Paragraph>
              You may terminate your account at any time by contacting our support team.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>14. Changes to Terms</SectionTitle>
            <Paragraph>
              We may update these Terms of Service from time to time. Material changes will be communicated via email or prominent 
              notice on our platform. Your continued use of our services after changes constitutes acceptance of the new terms.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>15. Governing Law</SectionTitle>
            <Paragraph>
              These Terms of Service shall be governed by and construed in accordance with applicable international laws. 
              Any disputes shall be resolved through binding arbitration or in the courts of appropriate jurisdiction.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>16. Contact Information</SectionTitle>
            <Paragraph>
              If you have questions about these Terms of Service, please contact us:
            </Paragraph>
            <ContactCard>
              <ContactIcon>
                <Mail size={24} color="#FAECEC" />
              </ContactIcon>
              <ContactInfo>
                <ContactTitle>Email Us</ContactTitle>
                <ContactEmail href="mailto:feedback@f01i.ai">feedback@f01i.ai</ContactEmail>
              </ContactInfo>
            </ContactCard>
          </Section>

          <Section>
            <Paragraph>
              By using our services, you acknowledge that you have read and understood these Terms of Service and agree to be 
              bound by them.
            </Paragraph>
          </Section>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default TermsOfService;
